import { useCallback, useEffect, useMemo, useState } from "react";
import { Header, ScrollProps } from "../../components/Header";
import { HomeHeroSearch } from "../../components/HomeHeroSearch";
import {
  PropertyCardCarousel,
  PropertyCardData,
} from "../../components/PropertyCardCarousel";
import { PropertyCardFeatured } from "../../components/PropertyCardFeatured";
import { SectionRails } from "../../components/SectionRails";
import { Footer } from "../../components/Footer";
import { LoadSpinner2 } from "../../components/LoadSpinner2";
import { useAuth } from "../../context/auth";
import { IBuildindCorretor, IPropsBanners } from "../../types";
import iconConfiabilidade from "../../assets/redesign/icons/confiabilidade.svg";
import iconPraticidade from "../../assets/redesign/icons/praticidade.svg";
import iconEmAlta from "../../assets/redesign/icons/em-alta.svg";
import {
  Container,
  DiffLeft,
  DiffRight,
  DiffText,
  DiffTitleRow,
  Differentials,
  DualHighlight,
  Page,
  SectionHeading,
  SectionPad,
  SectionPadBottom,
  SectionPadTall,
  TitleWithIcon,
} from "./styles";

const PLACEHOLDER_IMG = "/images/placeholder-image-nogueira.png";

const LITORAL_CITIES = [
  "Ipojuca",
  "Tamandaré",
  "Maragogi",
  "Japaratinga",
  "Cabo de Santo Agostinho",
  "Goiana",
  "Sirinhaém",
  "São José da Coroa Grande",
  "Ilha de Itamaracá",
].join(",");

function asBuildingList(data: unknown): IBuildindCorretor[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.imoveis)) return obj.imoveis as IBuildindCorretor[];
    if (Array.isArray(obj.data)) return obj.data as IBuildindCorretor[];
  }
  return [];
}

function toCardData(item: IBuildindCorretor): PropertyCardData {
  const imageUrl =
    (item.arquivos as any)?.[0]?.url_arquivo ||
    (item.arquivos as any)?.[0]?.url ||
    undefined;

  return {
    id: item.id,
    idDisplay: item.id_display,
    bairro: item.bairro,
    cidade: item.cidade,
    descricao: item.descricao,
    valorVenda: item.valor_venda,
    apBloco: item.ap_bloco,
    apNumero: item.ap_numero,
    imageUrl,
    isEmpreendimento: Boolean(item.is_empreendimento),
  };
}

export function Home() {
  const { user, api } = useAuth();

  const [featuredItems, setFeaturedItems] = useState<IBuildindCorretor[]>([]);
  const [emAltaItems, setEmAltaItems] = useState<IBuildindCorretor[]>([]);
  const [altoPadraoItems, setAltoPadraoItems] = useState<IBuildindCorretor[]>(
    [],
  );
  const [litoralItems, setLitoralItems] = useState<IBuildindCorretor[]>([]);
  const [totalEmpreendimentos, setTotalEmpreendimentos] = useState(0);
  const [banners, setBanners] = useState<IPropsBanners[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [scrollTop, setScrollTop] = useState<ScrollProps>({
    y: 0,
    lastY: 0,
  } as ScrollProps);
  const [show, setShow] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const y = event.currentTarget.scrollTop;
    setScrollTop((prev) => ({ y, lastY: prev.y }));
  };

  const loadBuildings = useCallback(async () => {
    setLoading(true);

    const baseParams: Record<string, string> = {
      portal: "hub",
      no_address: "1",
    };
    if (user?.id_corretor) {
      baseParams.id_corretor = String(user.id_corretor);
    }

    const fetchList = async (extra: Record<string, string> = {}) => {
      const res = await api.get(`/portal/building/list-building/0`, {
        params: { ...baseParams, ...extra },
      });
      return asBuildingList(res.data);
    };

    try {
      const [
        highlights,
        lancamentos,
        defaultList,
        altoPadraoRes,
        litoralZone,
        litoralCities,
        empreendimentosRes,
      ] = await Promise.allSettled([
        fetchList({ is_highlight: "1" }),
        fetchList({ is_launch: "1" }),
        fetchList(),
        fetchList({ min_sale_price: "1500000", is_sale: "1" }),
        fetchList({ address_zone: "litoral" }),
        fetchList({ address_city: LITORAL_CITIES }),
        api
          .get(`/portal/building/list-empreendimentos/`, {
            params: { portal: "hub", query: "" },
          })
          .then((res) => asBuildingList(res.data)),
      ]);

      const valueOf = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
        result.status === "fulfilled" ? result.value : fallback;

      const highlightList = valueOf(highlights, []);
      const lancamentosList = valueOf(lancamentos, []);
      const fallbackList = valueOf(defaultList, []);
      const altoPadraoList = valueOf(altoPadraoRes, []);
      const litoralByZone = valueOf(litoralZone, []);
      const litoralByCity = valueOf(litoralCities, []);
      const empreendimentos = valueOf(empreendimentosRes, []);

      const featuredList =
        highlightList.length > 0 ? highlightList : fallbackList;
      const litoralList =
        litoralByZone.length > 0 ? litoralByZone : litoralByCity;

      setFeaturedItems(featuredList.slice(0, 6));
      setEmAltaItems(lancamentosList.slice(0, 8));
      setAltoPadraoItems(altoPadraoList.slice(0, 4));
      setLitoralItems(litoralList.slice(0, 8));
      setTotalEmpreendimentos(
        empreendimentos.length > 0
          ? empreendimentos.length
          : fallbackList.length,
      );
    } catch (e: any) {
      console.log(e?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [user, api]);

  const loadBanners = useCallback(async () => {
    try {
      const res = await api.get("/portal/banners");
      setBanners(res.data ?? []);
    } catch (e: any) {
      console.log(e?.response?.data?.message);
    }
  }, [api]);

  useEffect(() => {
    loadBanners();
    loadBuildings();
  }, [loadBuildings, loadBanners]);

  const featuredList = useMemo(
    () => featuredItems.map(toCardData),
    [featuredItems],
  );
  const emAlta = useMemo(() => emAltaItems.map(toCardData), [emAltaItems]);
  const altoPadrao = useMemo(
    () => altoPadraoItems.map(toCardData),
    [altoPadraoItems],
  );
  const litoralList = useMemo(
    () => litoralItems.map(toCardData),
    [litoralItems],
  );

  const openCard = (item: PropertyCardData) => {
    const path = item.isEmpreendimento
      ? `/empreendimento/${item.id}`
      : `/imovel/${item.id}`;
    const full = user?.id_corretor
      ? `/perfil/${user.id_corretor}${path}`
      : path;
    window.open(full, "_blank", "noopener,noreferrer");
  };

  return (
    <Container onScroll={handleScroll}>
      <Header
        show={show}
        setShow={setShow}
        scroll={scrollTop}
        search={search}
        setSearch={setSearch}
      />

      {loading && <LoadSpinner2 />}

      {!loading && (
        <Page>
          <HomeHeroSearch
            banners={banners}
            totalEmpreendimentos={totalEmpreendimentos}
          />

          <Differentials>
            <DiffLeft>
              <h3>
                <b>Diferenciais</b>
                <br />
                Nogueira Vendas
              </h3>
              <DiffTitleRow>
                <img src={iconConfiabilidade} alt="" aria-hidden />
                <h4>CONFIABILIDADE</h4>
              </DiffTitleRow>
              <DiffText>
                O Hub Nogueira é mais que uma imobiliária. Somos um ecossistema
                de soluções para o mercado imobiliario, com DNA inovador e
                atuação forte no litoral, econômico, médio/alto padrão e imóveis
                prontos para morar. Unimos inteligência de mercado, times
                altamente especializados e uma cultura de alta performance,
                sempre de olho no futuro.
              </DiffText>
            </DiffLeft>
            <DiffRight>
              <DiffTitleRow>
                <img src={iconPraticidade} alt="" aria-hidden />
                <h4>PRATICIDADE</h4>
              </DiffTitleRow>
              <DiffText>
                Atuamos em parceria com as maiores construtoras do país e
                oferecemos experiências completas: do lançamento à entrega das
                chaves, com excelência comercial, tecnologia, crédito e
                marketing.
              </DiffText>
            </DiffRight>
          </Differentials>

          {featuredList.length > 0 && (
            <SectionPad>
              <SectionRails variant="featured">
                {featuredList.map((item) => (
                  <PropertyCardFeatured key={item.id} {...item} />
                ))}
              </SectionRails>
            </SectionPad>
          )}

          {emAlta.length > 0 && (
            <SectionPadTall>
              <SectionHeading>
                <TitleWithIcon>
                  <img src={iconEmAlta} alt="" aria-hidden />
                  <h4>Em alta</h4>
                </TitleWithIcon>
                <p>Novos lançamentos</p>
              </SectionHeading>
              <SectionRails variant="standard">
                {emAlta.map((item) => (
                  <PropertyCardCarousel key={item.id} {...item} />
                ))}
              </SectionRails>
            </SectionPadTall>
          )}

          {altoPadrao.length > 0 && (
            <SectionPad>
              <SectionHeading>
                <h4>Outros Destaques Alto Padrão</h4>
              </SectionHeading>
              <SectionRails variant="dual">
                {altoPadrao.map((item) => (
                  <DualHighlight key={item.id} onClick={() => openCard(item)}>
                    <img
                      src={item.imageUrl || PLACEHOLDER_IMG}
                      alt={item.descricao || "Imóvel"}
                    />
                  </DualHighlight>
                ))}
              </SectionRails>
            </SectionPad>
          )}

          {litoralList.length > 0 && (
            <SectionPadBottom>
              <SectionHeading>
                <h4>Litoral</h4>
                <p>Principais imóveis para ficar pertinho da praia</p>
              </SectionHeading>
              <SectionRails variant="standard">
                {litoralList.map((item) => (
                  <PropertyCardCarousel key={item.id} {...item} />
                ))}
              </SectionRails>
            </SectionPadBottom>
          )}

          <Footer />
        </Page>
      )}
    </Container>
  );
}
